import { Component, forwardRef, input, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { CommonValidators } from '../validators/common-validators';
import { uniqueId } from '@co-3/ctrl-ui/common';
import { NgClass } from '@angular/common';
import { ErrorMessagesComponent } from '../components/error-messages/error-messages.component';
import { ReactiveLabelDirective } from '../directives/reactive-label.directive';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Option = string | { label: string | number; value: any };

@Component({
    selector: 'app-select-with-other-option',
    templateUrl: './select-with-other-option.component.html',
    styleUrls: ['./select-with-other-option.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectWithOtherOptionComponent),
            multi: true,
        },
    ],
    imports: [NgClass, ErrorMessagesComponent, ReactiveFormsModule, ReactiveLabelDirective],
})
export class SelectWithOtherOptionComponent implements OnChanges, OnDestroy, ControlValueAccessor {
    @Input()
    public options: Option[] = [];
    @Input()
    public otherOptionLabel: string = 'Sonstiger';
    @Input()
    public pleaseEnterOtherLabel: string = 'Bitte gib den Hersteller an*';
    @Input()
    public placeholder: string = 'Bitte wählen';

    public errorMessages = input<string | { [key: string]: string }>({ required: 'Bitte gib den Hersteller an.' });

    protected selectErrorId = uniqueId('select-');
    protected otherErrorId = uniqueId('other-');

    public selectForm: FormGroup;
    public id = uniqueId();

    private onChange?: (value: unknown) => void;
    private onTouched?: () => void;
    private unsubscribe$: Subject<void> = new Subject<void>();

    public constructor(private formBuilder: FormBuilder) {
        this.selectForm = this.formBuilder.group({
            select: [null, Validators.required],
            other: ['', CommonValidators.shortSomething],
        });
        this.subscribeToFormChange();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['options']) {
            const currentOptionsValue = changes['options'].currentValue as Option[];
            if (currentOptionsValue?.length > 0) {
                this.selectForm.enable();
                const select = this.selectForm.get('select')?.value;
                if (
                    !currentOptionsValue.find((option: Option) =>
                        typeof option === 'string' ? option === select : option.value === select,
                    )
                ) {
                    this.selectForm.patchValue({
                        select: null,
                        other: '',
                    });
                }
            } else {
                this.selectForm.disable();
            }
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
    public registerOnChange(fn: (val: unknown) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public writeValue(value: unknown): void {
        if (typeof value === 'string' && value !== '') {
            if (this.hasOption(value)) {
                this.selectForm.patchValue({ select: value, other: '' });
            } else {
                this.selectForm.patchValue({ select: 'other', other: value });
            }
        } else {
            this.selectForm.patchValue({ select: null, other: '' });
        }
    }

    private subscribeToFormChange(): void {
        this.selectForm.valueChanges
            .pipe(
                tap((val) => {
                    if (val.select === 'other') {
                        this.focusOnOtherInput();
                    }
                }),
                map((val) => (val.select === 'other' ? val.other : val.select)),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((val) => {
                if (typeof this.onChange === 'function') {
                    this.onChange(val);
                }
            });
    }

    private hasOption(value): boolean {
        for (let i = 0; i < this.options.length; i++) {
            // TODO: this is really hacky, find a better way to do this:
            if (
                (typeof this.options[i] === 'string' && this.options[i] === value) ||
                (typeof this.options[i] !== 'string' && this.options[i]['value'] === value)
            ) {
                return true;
            }
        }
        return false;
    }

    private focusOnOtherInput(): void {
        const otherId = `other-input${this.id}`;
        // We need to wait for the input to be in the dom before focussing on it
        setTimeout(() => {
            const element = document.getElementById(otherId) as HTMLElement;
            element?.focus();
        }, 1);
    }

    public get otherSelected(): boolean {
        return this.selectForm.get('select').value === 'other';
    }
}
